import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../../lib/api";
import { useReader } from "../../context/ReaderContext";
import { BrandLoader } from "../../components/site/Logo";
import { ReaderPage, RequireReader, btnLine, btnMain, inputCls } from "./ReaderShell";
import { track } from "../../lib/analytics";

const PURPOSE = { account_terms: "Račun i uslovi korištenja", personalization: "Personalizacija vijesti", reading_history: "Historija čitanja", marketing: "Promotivne ponude" };

const Toggle = ({ label, desc, basis, on, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4 border-b border-rule py-5">
    <div><p className="font-semibold">{label}</p><p className="mt-1 text-sm text-muted-foreground">{desc}</p><p className="mt-1 text-xs text-muted-foreground">Pravni osnov: {basis}</p></div>
    <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${on ? "bg-pulse" : "bg-muted"}`} data-testid={testId}>
      <span className={`absolute left-0 top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  </div>
);

function Sessions() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/reader/sessions").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);
  return (
    <section className="mt-10" data-testid="reader-sessions">
      <h2 className="hl text-[1.15rem]">Aktivne prijave</h2>
      <ul className="mt-3 divide-y divide-rule text-sm">{items.map((s) => (
        <li key={s.id} className="flex items-center justify-between gap-3 py-3"><span className="min-w-0 truncate">{fmtDateTime(s.created_at)} · {s.user_agent.slice(0, 60)} {s.current && <b>(ovaj uređaj)</b>}</span>
          {!s.current && <button onClick={() => api.delete(`/reader/sessions/${s.id}`).then(load)} className="text-xs underline" data-testid={`revoke-session-${s.id}`}>Odjavi</button>}</li>))}</ul>
    </section>
  );
}

function Inner() {
  const [p, setP] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [del, setDel] = useState("");
  const { setReader, logout } = useReader();
  const nav = useNavigate();
  useEffect(() => {
    api.get("/reader/privacy").then((r) => setP(r.data));
  }, []);
  if (!p) return <ReaderPage title="Postavke privatnosti" testId="privacy-page"><BrandLoader /></ReaderPage>;
  const upd = async (patch) => {
    setP((await api.put("/reader/privacy", patch)).data);
    if (patch.personalization_enabled === true) track("personalization_enabled");
  };
  const exportData = async () => {
    const { data } = await api.get("/reader/export");
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    Object.assign(document.createElement("a"), { href: url, download: "balkanpuls-moji-podaci.json" }).click();
    URL.revokeObjectURL(url);
    toast.success("Izvoz podataka je preuzet");
  };
  const changePw = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reader/change-password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Lozinka je promijenjena; ostali uređaji su odjavljeni");
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  const remove = async (e) => {
    e.preventDefault();
    if (!window.confirm("Trajno obrisati račun i sve podatke profila? Ovo se ne može poništiti.")) return;
    try {
      await api.post("/reader/delete-account", { password: del });
      setReader(false);
      toast.success("Račun i podaci su trajno obrisani");
      nav("/");
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <ReaderPage title="Postavke privatnosti" intro="Ti odlučuješ šta se koristi. Profil čitalaca ne vide novinari, marketing ni partneri, a ne koristi se za ciljano oglašavanje." testId="privacy-page">
      <section className="max-w-2xl">
        <Toggle label="Personalizacija vijesti" desc="Koristi tvoja interesovanja, pokrajinu, boravišni status i državljanstvo za redoslijed vijesti. Isključeno = najnovije vijesti za sve." basis="privola (čl. 6. st. 1. tač. a GDPR)" on={p.personalization_enabled} onChange={(v) => upd({ personalization_enabled: v })} testId="privacy-personalization-toggle" />
        <Toggle label="Automatska historija čitanja" desc={`Bilježi otvorene članke da ih rjeđe ponavljamo. Nije potrebno za korištenje portala. Trenutno zapisa: ${p.history_count}. Isključivanje briše automatske zapise.`} basis="privola (čl. 6. st. 1. tač. a GDPR); brisanje nakon 180 dana" on={p.history_enabled} onChange={(v) => upd({ history_enabled: v })} testId="privacy-history-toggle" />
        <Toggle label="Promotivne ponude" desc="Odvojeno od vijesti i obavještenja. Trenutno ne šaljemo promotivne poruke." basis="privola (čl. 6. st. 1. tač. a GDPR)" on={p.marketing_consent} onChange={(v) => upd({ marketing_consent: v })} testId="privacy-marketing-toggle" />
        <div className="mt-8" data-testid="consent-history"><h2 className="hl text-[1.15rem]">Zabilježene saglasnosti</h2>
          <ul className="mt-3 space-y-1 text-sm">{p.consents.map((c) => <li key={c.purpose}>{PURPOSE[c.purpose] || c.purpose}: <b>{c.granted ? "da" : "ne"}</b> · {fmtDateTime(c.created_at)}</li>)}</ul></div>
        <div className="mt-8 flex flex-wrap gap-3"><button onClick={exportData} className={btnLine} data-testid="privacy-export-button">Preuzmi moje podatke (JSON)</button>
          <button onClick={async () => { await api.post("/reader/logout-all"); await logout(); nav("/prijava"); }} className={btnLine} data-testid="privacy-logout-all">Odjavi sve uređaje</button></div>
        <Sessions />
        <form onSubmit={changePw} className="mt-10 max-w-md space-y-3" data-testid="change-password-form"><h2 className="hl text-[1.15rem]">Promjena lozinke</h2>
          <input type="password" required placeholder="Trenutna lozinka" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="chpw-current" />
          <input type="password" required minLength={12} placeholder="Nova lozinka (min. 12 znakova)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} data-testid="chpw-new" />
          <button className={btnMain} data-testid="chpw-submit">Promijeni lozinku</button></form>
        <form onSubmit={remove} className="mt-12 max-w-md space-y-3 rounded-sm border border-destructive/40 p-5" data-testid="delete-account-form">
          <h2 className="font-display text-lg font-semibold text-destructive">Brisanje računa</h2>
          <p className="text-sm text-muted-foreground">Trajno brišemo račun, profil, sačuvane članke, historiju, obavještenja i saglasnosti. Zadržavamo samo anonimni zapis da je zahtjev izvršen (bez emaila).</p>
          <input type="password" required placeholder="Lozinka za potvrdu" value={del} onChange={(e) => setDel(e.target.value)} className={inputCls} data-testid="delete-password-input" />
          <button className="inline-flex min-h-[44px] items-center rounded-sm bg-destructive px-5 text-sm font-bold text-white" data-testid="delete-account-button">Trajno obriši račun</button>
        </form>
      </section>
    </ReaderPage>
  );
}

export default function Privatnost() {
  return <RequireReader><Inner /></RequireReader>;
}
